// TIBECO Cloud Upload Manager
import React, { useState, useRef, useCallback } from 'react';
import {
  Upload,
  FileText,
  Image,
  Video,
  Music,
  Archive,
  X,
  CheckCircle,
  AlertCircle,
  Clock,
  Zap,
  Folder,
  Plus,
  Settings,
  Pause,
  Play,
  Trash2,
  RefreshCw,
  Cloud,
  Server,
  Lock,
  Globe
} from 'lucide-react';

const CloudUpload = () => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploadSettings, setUploadSettings] = useState({
    encrypt: true,
    compress: false,
    backup: true,
    notification: true,
    destination: '/Proiecte 2025/'
  });
  const [isPaused, setIsPaused] = useState(false);

  const fileInputRef = useRef(null);

  // Sample upload queue data
  const [sampleFiles] = useState([
    {
      id: 1,
      name: 'Contract_Popescu_2025.pdf',
      size: 2.3 * 1024 * 1024, // bytes
      type: 'pdf',
      status: 'completed', // uploading, paused, completed, error
      progress: 100,
      speed: '2.1 MB/s',
      timeRemaining: 0,
      encrypted: true,
      destination: '/Contracte/2025/'
    },
    {
      id: 2,
      name: 'Fotografii_Montaj_Cluj.zip',
      size: 45.7 * 1024 * 1024,
      type: 'zip',
      status: 'uploading',
      progress: 67,
      speed: '3.8 MB/s',
      timeRemaining: 12,
      encrypted: true,
      destination: '/Fotografii/Montaj/'
    },
    {
      id: 3,
      name: 'Video_Prezentare_TIBECO.mp4',
      size: 127.3 * 1024 * 1024,
      type: 'mp4',
      status: 'paused',
      progress: 34,
      speed: '0 MB/s',
      timeRemaining: 0,
      encrypted: false,
      destination: '/Marketing/Video/'
    },
    {
      id: 4,
      name: 'Masuratori_Casa_Brasov.xlsx',
      size: 1.8 * 1024 * 1024,
      type: 'xlsx',
      status: 'error',
      progress: 0,
      speed: '0 MB/s',
      timeRemaining: 0,
      encrypted: true,
      destination: '/Masuratori/2025/',
      error: 'Conexiune întreruptă'
    },
    {
      id: 5,
      name: 'Backup_Database_TIBECO.sql',
      size: 89.2 * 1024 * 1024,
      type: 'sql',
      status: 'queued',
      progress: 0,
      speed: '0 MB/s',
      timeRemaining: 0,
      encrypted: true,
      destination: '/Backup/Database/'
    }
  ]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatTime = (seconds) => {
    if (seconds === 0) return '--';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf':
      case 'doc':
      case 'docx':
        return <FileText className="w-8 h-8 text-red-600" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <Image className="w-8 h-8 text-purple-600" />;
      case 'mp4':
      case 'avi':
      case 'mov':
        return <Video className="w-8 h-8 text-indigo-600" />;
      case 'mp3':
      case 'wav':
        return <Music className="w-8 h-8 text-pink-600" />;
      case 'zip':
      case 'rar':
        return <Archive className="w-8 h-8 text-yellow-600" />;
      case 'sql':
        return <Server className="w-8 h-8 text-blue-600" />;
      default:
        return <FileText className="w-8 h-8 text-gray-600" />;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'uploading':
        return <Upload className="w-5 h-5 text-blue-600 animate-pulse" />;
      case 'paused':
        return <Pause className="w-5 h-5 text-yellow-600" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      case 'queued':
        return <Clock className="w-5 h-5 text-gray-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500';
      case 'uploading':
        return 'bg-blue-500';
      case 'paused':
        return 'bg-yellow-500';
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-gray-300';
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      // Handle dropped files here
      console.log('Files dropped:', e.dataTransfer.files);
    }
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files) {
      // Handle selected files here
      console.log('Files selected:', e.target.files);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Upload Manager
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Gestionează încărcarea fișierelor în Amazon S3
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button className="flex items-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium rounded-lg transition-colors">
                <Settings className="w-4 h-4 mr-2" />
                Setări
              </button>
              <button 
                onClick={isPaused ? () => setIsPaused(false) : () => setIsPaused(true)}
                className={`flex items-center px-4 py-2 ${
                  isPaused 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-yellow-600 hover:bg-yellow-700'
                } text-white font-medium rounded-lg transition-colors`}
              >
                {isPaused ? <Play className="w-4 h-4 mr-2" /> : <Pause className="w-4 h-4 mr-2" />}
                {isPaused ? 'Continuă' : 'Pauză'}
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Upload Area & Settings */}
          <div className="lg:col-span-2 space-y-6">
            {/* Drag & Drop Upload Area */}
            <div
              className={`relative border-2 border-dashed rounded-xl p-12 text-center transition-all duration-200 ${
                dragActive
                  ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20'
                  : 'border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-600'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              
              <div className="flex flex-col items-center">
                <div className={`flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                  dragActive ? 'bg-cyan-100 dark:bg-cyan-900' : 'bg-gray-100 dark:bg-gray-700'
                }`}>
                  <Upload className={`w-8 h-8 ${
                    dragActive ? 'text-cyan-600' : 'text-gray-600'
                  }`} />
                </div>
                
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {dragActive ? 'Eliberează fișierele aici' : 'Încarcă fișiere'}
                </h3>
                
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {dragActive 
                    ? 'Eliberează fișierele pentru a începe încărcarea'
                    : 'Trage și plasează fișierele aici sau fă clic pentru a selecta'
                  }
                </p>
                
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-medium rounded-lg transition-colors"
                >
                  Selectează Fișiere
                </button>
                
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
                  Suportă: PDF, DOC, XLS, PPT, JPG, PNG, MP4, ZIP (Max: 500MB per fișier)
                </p>
              </div>
            </div>

            {/* Upload Queue */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Coadă Upload ({sampleFiles.length})
                  </h3>
                  <div className="flex items-center space-x-2">
                    <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                      <RefreshCw className="w-4 h-4" />
                    </button>
                    <button className="p-2 text-red-400 hover:text-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {sampleFiles.map((file) => (
                  <div key={file.id} className="p-6">
                    <div className="flex items-center space-x-4">
                      {/* File Icon */}
                      <div className="flex-shrink-0">
                        {getFileIcon(file.type)}
                      </div>

                      {/* File Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate mr-2">
                              {file.name}
                            </p>
                            {file.encrypted && <Lock className="w-4 h-4 text-green-600" />}
                          </div>
                          <div className="flex items-center space-x-2">
                            {getStatusIcon(file.status)}
                            <span className="text-xs text-gray-500 capitalize">{file.status}</span>
                          </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-300 ${getStatusColor(file.status)}`}
                            style={{ width: `${file.progress}%` }}
                          />
                        </div>

                        {/* File Details */}
                        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                          <div className="flex items-center space-x-4">
                            <span>{formatFileSize(file.size)}</span>
                            <span>→ {file.destination}</span>
                            {file.status === 'uploading' && (
                              <>
                                <span>{file.speed}</span>
                                <span>ETA: {formatTime(file.timeRemaining)}</span>
                              </>
                            )}
                          </div>
                          <div className="text-right">
                            {file.status === 'error' && (
                              <span className="text-red-600">{file.error}</span>
                            )}
                            <span className="ml-2">{file.progress}%</span>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex-shrink-0">
                        <div className="flex items-center space-x-2">
                          {file.status === 'uploading' && (
                            <button className="p-1 text-yellow-600 hover:text-yellow-700">
                              <Pause className="w-4 h-4" />
                            </button>
                          )}
                          {file.status === 'paused' && (
                            <button className="p-1 text-green-600 hover:text-green-700">
                              <Play className="w-4 h-4" />
                            </button>
                          )}
                          {file.status === 'error' && (
                            <button className="p-1 text-blue-600 hover:text-blue-700">
                              <RefreshCw className="w-4 h-4" />
                            </button>
                          )}
                          <button className="p-1 text-red-600 hover:text-red-700">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Settings Panel */}
          <div className="space-y-6">
            {/* Upload Settings */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Setări Upload
              </h3>
              
              <div className="space-y-4">
                {/* Destination */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Destinație
                  </label>
                  <div className="relative">
                    <Folder className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <select 
                      value={uploadSettings.destination}
                      onChange={(e) => setUploadSettings({...uploadSettings, destination: e.target.value})}
                      className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                    >
                      <option value="/Proiecte 2025/">/Proiecte 2025/</option>
                      <option value="/Contracte/">/Contracte/</option>
                      <option value="/Fotografii/">/Fotografii/</option>
                      <option value="/Documente/">/Documente/</option>
                      <option value="/Backup/">/Backup/</option>
                    </select>
                  </div>
                </div>

                {/* Settings Checkboxes */}
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={uploadSettings.encrypt}
                      onChange={(e) => setUploadSettings({...uploadSettings, encrypt: e.target.checked})}
                      className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Criptează fișierele</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={uploadSettings.compress}
                      onChange={(e) => setUploadSettings({...uploadSettings, compress: e.target.checked})}
                      className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Comprimare automată</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={uploadSettings.backup}
                      onChange={(e) => setUploadSettings({...uploadSettings, backup: e.target.checked})}
                      className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Backup automat</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={uploadSettings.notification}
                      onChange={(e) => setUploadSettings({...uploadSettings, notification: e.target.checked})}
                      className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Notificări email</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Upload Statistics */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Statistici
              </h3>
              
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Fișiere în coadă:</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">5</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Mărime totală:</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">266.3 MB</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Finalizate:</span>
                  <span className="text-sm font-medium text-green-600">1</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">În progres:</span>
                  <span className="text-sm font-medium text-blue-600">1</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Erori:</span>
                  <span className="text-sm font-medium text-red-600">1</span>
                </div>
              </div>
            </div>

            {/* S3 Connection Status */}
            <div className="bg-gradient-to-r from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20 rounded-xl border border-orange-200 dark:border-orange-800 p-4">
              <div className="flex items-center mb-2">
                <Zap className="w-5 h-5 text-orange-600 mr-2" />
                <span className="font-semibold text-gray-900 dark:text-white">Amazon S3</span>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <p>Status: <span className="text-green-600 font-medium">Conectat</span></p>
                <p>Bucket: tibeco-storage-ro</p>
                <p>Regiune: eu-central-1</p>
                <p>Viteză upload: 3.8 MB/s</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CloudUpload;
